import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { RaceGoal } from '../../types';
import Navbar from '../Layout/Navbar';
import { useAuth } from '../../context/AuthContext';
import ContinueRaceModal from '../Training/ContinueRaceModal';
import './RaceGoalSetup.css';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const METHODOLOGY_INFO: Record<string, { name: string; tagline: string; description: string; icon: string }> = {
  daniels:           { icon: '📐', name: 'Daniels Running Formula',      tagline: 'Quality over quantity — precise pacing, structured intervals',     description: "You're an experienced runner with consistent mileage. Daniels' VDOT system prioritises 1–2 quality sessions per week at precise effort levels, with easy running filling the rest." },
  pfitzinger:        { icon: '🏔', name: 'Pfitzinger Advanced Marathoning', tagline: 'High mileage, double quality sessions, recovery-focused',         description: "You're a high-mileage runner ready for an elite approach. Pfitzinger stresses general aerobic development with medium-long mid-week runs and two weekly quality sessions." },
  hansons:           { icon: '⚙️', name: "Hanson's Method",               tagline: 'Cumulative fatigue, 6-day training, shorter long runs',            description: "You run 6 days a week and are ready to train on tired legs. Hanson's builds race-specific fatigue through consistent daily running rather than one very long run." },
  first:             { icon: '🎯', name: 'FIRST (Furman)',                 tagline: '3 quality runs per week, cross-train on off days',                 description: "You run 3 days a week so every session needs to count. FIRST gives each run a specific purpose — speed, tempo, or long — with cross-training on off days." },
  higdon_novice:     { icon: '🌱', name: 'Higdon Novice',                 tagline: 'Conservative mileage build, finishing is the goal',                description: "You're building your base and your main goal is crossing the finish line. Higdon Novice grows mileage gradually with no speedwork — consistency over intensity." },
  higdon_intermediate: { icon: '📈', name: 'Higdon Intermediate',         tagline: 'Consistent base, mid-week medium runs, weekly long run',           description: "You have a solid base and are targeting a specific finish time. Higdon Intermediate adds a pace run mid-week for runners ready to race rather than just finish." },
};
const DAY_SHORT: Record<string, string> = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu',
  Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun'
};

// [distance_threshold_miles, min_weeks, label]
const LEAD_TIME_MINIMUMS: [number, number, string][] = [
  [3.2,   2, '5K'],
  [6.5,   3, '10K'],
  [13.5,  4, 'half marathon'],
  [26.5,  6, 'full marathon'],
  [33.0,  6, '50K'],
  [52.0,  8, '50-miler'],
  [999,   8, '100-miler'],
];

function checkLeadTime(raceDateStr: string, distanceMiles: number): string | null {
  if (!raceDateStr || !distanceMiles) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const race = new Date(raceDateStr + 'T00:00:00');
  const weeksUntil = Math.floor((race.getTime() - today.getTime()) / (7 * 24 * 60 * 60 * 1000));
  if (weeksUntil < 0) return null; // already handled by date validation
  for (const [threshold, minWeeks, label] of LEAD_TIME_MINIMUMS) {
    if (distanceMiles <= threshold) {
      if (weeksUntil < minWeeks) {
        return `⚠️ Only ${weeksUntil} week${weeksUntil !== 1 ? 's' : ''} until your race. A ${label} needs at least ${minWeeks} weeks of preparation. We'll generate a race-prep plan to help you arrive feeling your best, but significant fitness gains won't be possible in this timeframe.`;
      }
      return null;
    }
  }
  return null;
}

const RaceGoalSetup: React.FC = () => {
  const navigate = useNavigate();
  const { setUnitPreference } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [mileageWarning, setMileageWarning] = useState<string | null>(null);
  const [leadTimeWarning, setLeadTimeWarning] = useState<string | null>(null);
  const [unitPref, setUnitPref] = useState<'imperial' | 'metric'>('imperial');
  const [trainingMethodology, setTrainingMethodology] = useState<string | null>(null);
  const [lastRace, setLastRace] = useState<any | null>(null);
  const [showForm, setShowForm] = useState(true);
  // Snapshot of the active race at load, to detect "switching to a later race"
  // and offer continuation instead of a fresh rebuild.
  const [existingRace, setExistingRace] = useState<{ name: string; date: string } | null>(null);
  const [showContinueModal, setShowContinueModal] = useState(false);

  // Schedule preferences
  const [selectedRunDays, setSelectedRunDays] = useState<string[]>([]);
  const [selectedStrengthDays, setSelectedStrengthDays] = useState<string[]>([]);
  const [selectedCyclingDays, setSelectedCyclingDays] = useState<string[]>([]);
  const [selectedWalkingDays, setSelectedWalkingDays] = useState<string[]>([]);
  const [selectedRestDays, setSelectedRestDays] = useState<string[]>([]);
  const [longRunDay, setLongRunDay] = useState<string>('Sunday');
  const [qualityWorkoutDay, setQualityWorkoutDay] = useState<string>('Wednesday');

  const [formData, setFormData] = useState<RaceGoal>({
    race_name: '',
    race_date: '',
    race_distance_miles: undefined as any,
    race_elevation_gain_ft: undefined,
    race_max_altitude_ft: undefined,
    goal_finish_time_seconds: undefined,
    max_heart_rate: undefined,
    running_days_per_week: undefined as any,
    strength_days_per_week: undefined as any,
    current_weekly_mileage: undefined,
    mileage_adapt_threshold: 75
  });

  const [goalTime, setGoalTime] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadExistingGoal();
  }, []);

  useEffect(() => {
    setLeadTimeWarning(checkLeadTime(formData.race_date, formData.race_distance_miles));
  }, [formData.race_date, formData.race_distance_miles]);

  const isMetric = unitPref === 'metric';

  // Snapshot of the goal as loaded, to decide whether a save changes the plan.
  const savedGoalRef = useRef<Partial<RaceGoal> | null>(null);

  // Only these fields change what the generator produces for this week.
  // Notes, goal time, run days etc. just save and apply on the next generation.
  const planAffectingChange = (next: Partial<RaceGoal>) => {
    const prev = savedGoalRef.current;
    if (!prev) return true;  // first-time goal — always generate
    return (['race_name', 'race_date', 'race_distance_miles', 'max_weekly_mileage'] as const)
      .some(k => (prev[k] ?? null) !== (next[k] ?? null));
  };

  const loadExistingGoal = async () => {
    try {
      const goal = await api.getRaceGoal();
      const pref = (goal.unit_preference as 'imperial' | 'metric') || 'imperial';
      setUnitPref(pref);
      setFormData(goal);
      savedGoalRef.current = goal;

      // Remember the active (future) race so we can offer continuation if the
      // user switches to a later one.
      if (goal.race_name && goal.race_date) {
        const today = new Date().toISOString().slice(0, 10);
        if (goal.race_date >= today) {
          setExistingRace({ name: goal.race_name, date: goal.race_date });
        }
      }

      // Convert seconds to HH:MM:SS
      if (goal.goal_finish_time_seconds) {
        const hours = Math.floor(goal.goal_finish_time_seconds / 3600);
        const minutes = Math.floor((goal.goal_finish_time_seconds % 3600) / 60);
        const seconds = goal.goal_finish_time_seconds % 60;
        setGoalTime({ hours, minutes, seconds });
      }
      if (goal.preferred_run_days) setSelectedRunDays(goal.preferred_run_days);
      if (goal.preferred_strength_days) setSelectedStrengthDays(goal.preferred_strength_days);
      if (goal.preferred_cycling_days) setSelectedCyclingDays(goal.preferred_cycling_days);
      if (goal.preferred_walking_days) setSelectedWalkingDays(goal.preferred_walking_days);
      if (goal.preferred_rest_days) setSelectedRestDays(goal.preferred_rest_days);
      if (goal.long_run_day) setLongRunDay(goal.long_run_day);
      if (goal.quality_workout_day) setQualityWorkoutDay(goal.quality_workout_day);
      if (goal.training_methodology) setTrainingMethodology(goal.training_methodology);

      // If race date is in the past, look for the matching TrainingPlan record
      // so we can show a celebration / expired card instead of pre-filled form.
      if (goal.race_date) {
        const today = new Date().toISOString().slice(0, 10);
        if (goal.race_date < today) {
          try {
            const history = await api.getRaceHistory();
            const matchingPlan = (history.races || []).find(
              (r: any) =>
                r.race_name === goal.race_name &&
                r.race_date === goal.race_date,
            );
            if (matchingPlan) {
              setLastRace(matchingPlan);
              setShowForm(false);
            }
          } catch {
            // race history unavailable — fall through to normal form
          }
        }
      }
    } catch (err) {
      // No existing goal, that's fine
    } finally {
      setLoading(false);
    }
  };

  const startNewRaceGoal = () => {
    setFormData({
      race_name: '',
      race_date: '',
      race_distance_miles: undefined as any,
      race_elevation_gain_ft: undefined,
      race_max_altitude_ft: undefined,
      goal_finish_time_seconds: undefined,
      max_heart_rate: formData.max_heart_rate, // preserve physiology
      running_days_per_week: formData.running_days_per_week, // preserve schedule
      strength_days_per_week: formData.strength_days_per_week,
      current_weekly_mileage: formData.current_weekly_mileage,
      mileage_adapt_threshold: formData.mileage_adapt_threshold,
    } as any);
    setGoalTime({ hours: 0, minutes: 0, seconds: 0 });
    setShowForm(true);
  };

  type DayRole = 'run' | 'strength' | 'cycling' | 'walking' | 'rest';
  const toggleDay = (day: string, type: DayRole) => {
    const set = (setter: React.Dispatch<React.SetStateAction<string[]>>, mine: boolean) =>
      setter(prev => mine ? (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]) : prev.filter(d => d !== day));
    set(setSelectedRunDays, type === 'run');
    set(setSelectedStrengthDays, type === 'strength');
    set(setSelectedCyclingDays, type === 'cycling');
    set(setSelectedWalkingDays, type === 'walking');
    set(setSelectedRestDays, type === 'rest');
  };

  const getDayType = (day: string) => {
    if (selectedRunDays.includes(day)) return 'run';
    if (selectedStrengthDays.includes(day)) return 'strength';
    if (selectedCyclingDays.includes(day)) return 'cycling';
    if (selectedWalkingDays.includes(day)) return 'walking';
    if (selectedRestDays.includes(day)) return 'rest';
    return 'unassigned';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    // For number inputs, allow empty string or valid numbers
    if (type === 'number') {
      if (value === '') {
        setFormData(prev => ({ ...prev, [name]: undefined }));
        if (name === 'max_weekly_mileage') {
          setMileageWarning(null);
        }
      } else {
        const numValue = name.includes('miles') || name.includes('mileage')
          ? parseFloat(value)
          : parseInt(value);

        // Only update if it's a valid number
        if (!isNaN(numValue)) {
          setFormData(prev => {
            const updated = { ...prev, [name]: numValue };

            // Check max weekly mileage constraint.
            // 2.5x assumes a ~6-day/week runner; scale by run days so a 3-day
            // plan isn't told it needs an unreachable peak. Falls back to 6.
            if (name === 'max_weekly_mileage' && updated.race_distance_miles) {
              const runDays = updated.running_days_per_week || 6;
              const recommendedMax = updated.race_distance_miles * 2.5 * (runDays / 6);
              const constraint = numValue / recommendedMax;

              if (numValue < recommendedMax) {
                const confidenceImpact = constraint < 0.6 ? 'significantly reduced' : constraint < 0.8 ? 'moderately reduced' : 'slightly reduced';
                setMileageWarning(
                  `⚠️ To optimally train for ${updated.race_distance_miles} miles, you should handle ~${Math.round(recommendedMax)} miles/week at peak. ` +
                  `With your ${numValue} mile limit, your confidence of hitting your goal will be ${confidenceImpact}. ` +
                  `After saving, use "🔄 Regenerate Plan" on your training page to adapt your current week's training to this constraint.`
                );
              } else {
                setMileageWarning(null);
              }
            }

            return updated;
          });
        }
      }
    } else {
      // For text inputs, just use the value
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGoalTime(prev => ({
      ...prev,
      [name]: parseInt(value) || 0
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    // Validation
    const errors: Record<string, string> = {};

    if (!formData.race_distance_miles || formData.race_distance_miles <= 0) {
      errors.race_distance_miles = 'Race distance is required and must be greater than 0';
    }
    if (formData.current_weekly_mileage !== undefined && formData.current_weekly_mileage < 0) {
      errors.current_weekly_mileage = 'Cannot be negative';
    }
    if (formData.max_weekly_mileage !== undefined && formData.max_weekly_mileage < 0) {
      errors.max_weekly_mileage = 'Cannot be negative';
    }
    if (formData.max_weekly_mileage !== undefined && formData.current_weekly_mileage !== undefined &&
        formData.max_weekly_mileage < formData.current_weekly_mileage) {
      errors.max_weekly_mileage = 'Must be ≥ current weekly mileage';
    }
    if (formData.race_elevation_gain_ft !== undefined && formData.race_elevation_gain_ft < 0) {
      errors.race_elevation_gain_ft = 'Cannot be negative';
    }
    if (formData.max_heart_rate !== undefined && (formData.max_heart_rate < 100 || formData.max_heart_rate > 220)) {
      errors.max_heart_rate = 'Must be between 100 and 220 bpm';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fix the errors highlighted below');
      setSaving(false);
      return;
    }
    setFieldErrors({});

    // If the user is switching to a race LATER than their current one, offer to
    // continue (keep fitness) rather than rebuild from base.
    const switchingToLaterRace =
      !!existingRace &&
      !!formData.race_date &&
      formData.race_date > existingRace.date &&
      (formData.race_name !== existingRace.name || formData.race_date !== existingRace.date);
    if (switchingToLaterRace) {
      setSaving(false);
      setShowContinueModal(true);
      return;
    }

    await doSave();
  };

  const buildSubmitData = () => {
    const goal_finish_time_seconds =
      goalTime.hours * 3600 + goalTime.minutes * 60 + goalTime.seconds;
    return {
      ...formData,
      unit_preference: unitPref,
      goal_finish_time_seconds: goal_finish_time_seconds > 0 ? goal_finish_time_seconds : undefined,
      preferred_run_days: selectedRunDays,
      preferred_strength_days: selectedStrengthDays,
      preferred_cycling_days: selectedCyclingDays,
      preferred_walking_days: selectedWalkingDays,
      preferred_rest_days: selectedRestDays,
      long_run_day: longRunDay,
      quality_workout_day: qualityWorkoutDay,
    };
  };

  // Continuation: keep the current cycle, roll into the (later) race.
  const doContinueFromForm = async () => {
    const sd = buildSubmitData();
    await api.continueIntoNextRace({
      race_name: sd.race_name,
      race_date: sd.race_date,
      race_distance_miles: sd.race_distance_miles,
      ...(sd.race_elevation_gain_ft != null ? { race_elevation_gain_ft: sd.race_elevation_gain_ft } : {}),
      ...(sd.goal_finish_time_seconds != null ? { goal_finish_time_seconds: sd.goal_finish_time_seconds } : {}),
    });
    setUnitPreference(unitPref);
    navigate('/training-plan');
  };

  const doSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const submitData = buildSubmitData();

      await api.setRaceGoal(submitData);
      setUnitPreference(unitPref);

      // Regenerate the current week only when the race itself or the weekly
      // mileage cap changed — those change what the generator produces. Other
      // edits (notes, goal time, run days) just save; the next generation
      // picks them up. Regenerating for a notes edit threw away a reviewed
      // week to rebuild the identical one.
      const regenerate = planAffectingChange(submitData);
      savedGoalRef.current = submitData;
      try {
        if (regenerate) await api.generatePlan(true);  // regenerate_current=true
        navigate('/training-plan');
        return;
      } catch (genErr: any) {
        // Save succeeded; only the regenerate failed. Surface that but
        // don't lose the save success state.
        setError(`Saved, but plan regeneration failed: ${genErr?.message || genErr}`);
      }

      // Fallback (regenerate failed or skipped): show success here
      setShowContinueModal(false);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => setSuccess(false), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to save race goal');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Loading...</div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      {showContinueModal && existingRace && (
        <ContinueRaceModal
          currentRaceName={existingRace.name}
          newRaceName={formData.race_name || 'your new race'}
          canContinue={true}
          onContinue={doContinueFromForm}
          onFresh={doSave}
          onClose={() => setShowContinueModal(false)}
        />
      )}
      <div className="race-goal-setup">
        <div className="race-goal-container">
          <h1>Race Goal</h1>
          <p className="subtitle">Set your target race to get a personalized training plan</p>

          {!showForm && lastRace && lastRace.status === 'completed' && (() => {
            const fmtTime = (s: number | null) => {
              if (!s) return '—';
              const h = Math.floor(s / 3600);
              const m = Math.floor((s % 3600) / 60);
              const sec = s % 60;
              return h > 0
                ? `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
                : `${m}:${sec.toString().padStart(2, '0')}`;
            };
            const predicted = lastRace.predicted_p50_seconds_at_race;
            const actual = lastRace.actual_finish_seconds;
            let deltaText = '';
            let deltaClass = '';
            if (predicted && actual) {
              const diff = actual - predicted;
              const abs = Math.abs(diff);
              const m = Math.floor(abs / 60);
              const s = Math.floor(abs % 60);
              const sign = diff < 0 ? '−' : '+';
              const label = diff < 0 ? 'faster' : 'slower';
              deltaText = `${sign}${m}:${s.toString().padStart(2, '0')} ${label} than predicted`;
              deltaClass = diff < 0 ? 'rh-delta-good' : 'rh-delta-bad';
            }
            return (
              <div className="race-complete-card">
                <div className="race-complete-icon">🏁</div>
                <h2 className="race-complete-title">{lastRace.race_name} complete</h2>
                <p className="race-complete-finish">
                  You finished in <strong>{fmtTime(actual)}</strong>
                  {deltaText && <> — <span className={deltaClass}>{deltaText}</span></>}
                </p>
                <div className="race-complete-actions">
                  <button className="btn-primary" onClick={startNewRaceGoal}>
                    Set Your Next Race
                  </button>
                  <button className="btn-secondary" onClick={() => navigate('/race-history')}>
                    View Race History
                  </button>
                </div>
              </div>
            );
          })()}

          {!showForm && lastRace && lastRace.status === 'expired' && (
            <div className="race-expired-card">
              <div className="race-complete-icon">⏰</div>
              <h2 className="race-complete-title">Your race day has passed</h2>
              <p className="race-complete-finish">
                {lastRace.race_name} was on {lastRace.race_date}. We didn't find a matching activity in your sync.
              </p>
              <p className="race-complete-finish">
                If you ran it, you can mark the workout as your race. Otherwise, time to set your next goal.
              </p>
              <div className="race-complete-actions">
                <button className="btn-secondary" onClick={() => navigate('/workouts')}>
                  I ran it — find my workout
                </button>
                <button className="btn-primary" onClick={startNewRaceGoal}>
                  Set a New Race Goal
                </button>
              </div>
            </div>
          )}

          {showForm && trainingMethodology && METHODOLOGY_INFO[trainingMethodology] && (() => {
            const m = METHODOLOGY_INFO[trainingMethodology];
            return (
              <div className="methodology-card">
                <div className="methodology-card-header">
                  <span className="methodology-card-icon">{m.icon}</span>
                  <div>
                    <div className="methodology-card-label">Your Training Framework</div>
                    <div className="methodology-card-name">{m.name}</div>
                  </div>
                </div>
                <div className="methodology-card-tagline">{m.tagline}</div>
                <div className="methodology-card-desc">{m.description}</div>
              </div>
            );
          })()}

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">✅ Race goal saved successfully!</div>}

          {showForm && (
          <form onSubmit={handleSubmit} className="race-goal-form">
            {/* Units preference */}
            <section className="form-section">
              <h2>Units</h2>
              <div className="form-group">
                <label>Preferred Unit System</label>
                <select
                  value={unitPref}
                  onChange={e => setUnitPref(e.target.value as 'imperial' | 'metric')}
                >
                  <option value="imperial">Imperial (miles, feet)</option>
                  <option value="metric">Metric (km, meters)</option>
                </select>
              </div>
            </section>

            {/* Race Details */}
            <section className="form-section">
              <h2>Race Details</h2>

              <div className="form-group">
                <label>Race Name *</label>
                <input
                  type="text"
                  name="race_name"
                  value={formData.race_name}
                  onChange={handleChange}
                  placeholder="e.g., Big Sur 21 Miler"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Race Date *</label>
                  <input
                    type="date"
                    name="race_date"
                    value={formData.race_date}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Distance ({isMetric ? 'km' : 'miles'}) *</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    name="race_distance_miles"
                    value={isMetric && formData.race_distance_miles
                      ? parseFloat((formData.race_distance_miles * 1.60934).toFixed(2))
                      : (formData.race_distance_miles || '')}
                    onChange={e => {
                      if (e.target.value === '') {
                        setFormData(prev => ({ ...prev, race_distance_miles: undefined as any }));
                        setFieldErrors(prev => ({ ...prev, race_distance_miles: 'Race distance is required' }));
                        return;
                      }
                      setFieldErrors(prev => { const n = { ...prev }; delete n.race_distance_miles; return n; });
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v)) setFormData(prev => ({ ...prev, race_distance_miles: isMetric ? v / 1.60934 : v }));
                    }}
                    required
                    className={fieldErrors.race_distance_miles ? 'error-input' : ''}
                  />
                  {fieldErrors.race_distance_miles && (
                    <span className="field-error-msg">{fieldErrors.race_distance_miles}</span>
                  )}
                </div>
              </div>

              {leadTimeWarning && (
                <div className="lead-time-warning">{leadTimeWarning}</div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Elevation Gain ({isMetric ? 'm' : 'ft'})</label>
                  <input
                    type="number"
                    min="0"
                    name="race_elevation_gain_ft"
                    value={isMetric && formData.race_elevation_gain_ft
                      ? Math.round(formData.race_elevation_gain_ft * 0.3048)
                      : formData.race_elevation_gain_ft || ''}
                    onChange={e => {
                      if (e.target.value === '') {
                        setFormData(prev => ({ ...prev, race_elevation_gain_ft: undefined }));
                        return;
                      }
                      const v = parseInt(e.target.value);
                      setFormData(prev => ({ ...prev, race_elevation_gain_ft: isNaN(v) ? undefined : (isMetric ? Math.round(v / 0.3048) : v) }));
                    }}
                    placeholder={isMetric ? 'e.g., 600' : 'e.g., 2000'}
                    className={fieldErrors.race_elevation_gain_ft ? 'error-input' : ''}
                  />
                  {fieldErrors.race_elevation_gain_ft && (
                    <span className="field-error-msg">{fieldErrors.race_elevation_gain_ft}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Max Altitude ({isMetric ? 'm' : 'ft'})</label>
                  <input
                    type="number"
                    min="0"
                    name="race_max_altitude_ft"
                    value={isMetric && formData.race_max_altitude_ft
                      ? Math.round(formData.race_max_altitude_ft * 0.3048)
                      : formData.race_max_altitude_ft || ''}
                    onChange={e => {
                      if (e.target.value === '') {
                        setFormData(prev => ({ ...prev, race_max_altitude_ft: undefined }));
                        return;
                      }
                      const v = parseInt(e.target.value);
                      setFormData(prev => ({ ...prev, race_max_altitude_ft: isNaN(v) ? undefined : (isMetric ? Math.round(v / 0.3048) : v) }));
                    }}
                    placeholder={isMetric ? 'e.g., 150' : 'e.g., 500'}
                    className={fieldErrors.race_max_altitude_ft ? 'error-input' : ''}
                  />
                  {fieldErrors.race_max_altitude_ft && (
                    <span className="field-error-msg">{fieldErrors.race_max_altitude_ft}</span>
                  )}
                </div>
              </div>
            </section>

            {/* Goal Time */}
            <section className="form-section">
              <h2>Goal Finish Time</h2>
              <div className="time-inputs">
                <div className="form-group">
                  <label>Hours</label>
                  <input
                    type="number"
                    name="hours"
                    min="0"
                    value={goalTime.hours}
                    onChange={handleTimeChange}
                  />
                </div>
                <span className="time-separator">:</span>
                <div className="form-group">
                  <label>Minutes</label>
                  <input
                    type="number"
                    name="minutes"
                    min="0"
                    max="59"
                    value={goalTime.minutes}
                    onChange={handleTimeChange}
                  />
                </div>
                <span className="time-separator">:</span>
                <div className="form-group">
                  <label>Seconds</label>
                  <input
                    type="number"
                    name="seconds"
                    min="0"
                    max="59"
                    value={goalTime.seconds}
                    onChange={handleTimeChange}
                  />
                </div>
              </div>
            </section>

            {/* Training Availability */}
            <section className="form-section">
              <h2>Training Availability</h2>

              <div className="form-row">
                <div className="form-group">
                  <label>Running Days/Week</label>
                  <input
                    type="number"
                    min="1"
                    max="7"
                    name="running_days_per_week"
                    value={formData.running_days_per_week || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Strength Days/Week</label>
                  <input
                    type="number"
                    min="0"
                    max="7"
                    name="strength_days_per_week"
                    value={formData.strength_days_per_week || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </section>

            {/* Weekly Schedule Preferences */}
            <section className="form-section">
              <h2>Weekly Schedule</h2>
              <p className="section-hint">Click a day to assign it. Each day can only have one role.</p>
              <div className="sched-days-grid">
                {DAYS_OF_WEEK.map(day => {
                  const type = getDayType(day);
                  return (
                    <div key={day} className={`sched-day-card ${type}`}>
                      <div className="sched-day-name">{DAY_SHORT[day]}</div>
                      <button type="button" className={`sched-btn run-btn ${type === 'run' ? 'active' : ''}`}
                        onClick={() => toggleDay(day, 'run')}>🏃 Run</button>
                      <button type="button" className={`sched-btn strength-btn ${type === 'strength' ? 'active' : ''}`}
                        onClick={() => toggleDay(day, 'strength')}>💪 Strength</button>
                      <button type="button" className={`sched-btn cross-btn ${type === 'cycling' ? 'active' : ''}`}
                        onClick={() => toggleDay(day, 'cycling')}>🚴 Cycling</button>
                      <button type="button" className={`sched-btn cross-btn ${type === 'walking' ? 'active' : ''}`}
                        onClick={() => toggleDay(day, 'walking')}>🚶 Walking</button>
                      <button type="button" className={`sched-btn rest-btn ${type === 'rest' ? 'active' : ''}`}
                        onClick={() => toggleDay(day, 'rest')}>😴 Rest</button>
                    </div>
                  );
                })}
              </div>

              {selectedRunDays.length >= 2 && (
                <div className="form-row" style={{ marginTop: '1.25rem' }}>
                  <div className="form-group">
                    <label>Long run day</label>
                    <select value={longRunDay} onChange={e => setLongRunDay(e.target.value)}>
                      {selectedRunDays.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Quality workout day (tempo / intervals)</label>
                    <select value={qualityWorkoutDay} onChange={e => setQualityWorkoutDay(e.target.value)}>
                      {selectedRunDays.filter(d => d !== longRunDay).map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </section>

            {/* Current Fitness */}
            <section className="form-section">
              <h2>Current Fitness & Capacity</h2>

              <div className="form-row">
                <div className="form-group">
                  <label>Current Weekly {isMetric ? 'Volume (km)' : 'Mileage'}</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    name="current_weekly_mileage"
                    value={isMetric && formData.current_weekly_mileage
                      ? parseFloat((formData.current_weekly_mileage * 1.60934).toFixed(1))
                      : formData.current_weekly_mileage || ''}
                    onChange={e => {
                      if (e.target.value === '') {
                        setFormData(prev => ({ ...prev, current_weekly_mileage: undefined }));
                        return;
                      }
                      const v = parseFloat(e.target.value);
                      setFormData(prev => ({ ...prev, current_weekly_mileage: isNaN(v) ? undefined : (isMetric ? v / 1.60934 : v) }));
                    }}
                    placeholder={isMetric ? 'e.g., 40' : 'e.g., 25'}
                    className={fieldErrors.current_weekly_mileage ? 'error-input' : ''}
                  />
                  {fieldErrors.current_weekly_mileage && (
                    <span className="field-error-msg">{fieldErrors.current_weekly_mileage}</span>
                  )}
                  <span className="field-hint">Your typical weekly running volume</span>
                </div>

                <div className="form-group">
                  <label>Maximum Weekly {isMetric ? 'Volume (km)' : 'Mileage'}</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    name="max_weekly_mileage"
                    value={isMetric && formData.max_weekly_mileage
                      ? parseFloat((formData.max_weekly_mileage * 1.60934).toFixed(1))
                      : formData.max_weekly_mileage || ''}
                    onChange={e => {
                      if (e.target.value === '') {
                        setFormData(prev => ({ ...prev, max_weekly_mileage: undefined }));
                        return;
                      }
                      const v = parseFloat(e.target.value);
                      setFormData(prev => ({ ...prev, max_weekly_mileage: isNaN(v) ? undefined : (isMetric ? v / 1.60934 : v) }));
                    }}
                    placeholder={isMetric ? 'e.g., 80' : 'e.g., 50'}
                    className={fieldErrors.max_weekly_mileage ? 'error-input' : ''}
                  />
                  {fieldErrors.max_weekly_mileage && (
                    <span className="field-error-msg">{fieldErrors.max_weekly_mileage}</span>
                  )}
                  <span className="field-hint">Maximum you can safely handle at peak training</span>
                  {mileageWarning && (
                    <div className="mileage-warning">{mileageWarning}</div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group--full">
                  <label>Notes for the coach about this training block <span className="field-opt">(optional)</span></label>
                  <textarea
                    name="race_notes"
                    rows={2}
                    maxLength={1000}
                    value={formData.race_notes || ''}
                    onChange={e => setFormData(prev => ({ ...prev, race_notes: e.target.value }))}
                    placeholder="e.g. On vacation Oct 25 – Nov 7, little or no running."
                  />
                  <span className="field-hint">The AI coach reads this when building every week of your plan</span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Reduce plan when I'm below</label>
                  <select
                    name="mileage_adapt_threshold"
                    value={formData.mileage_adapt_threshold ?? 75}
                    onChange={handleChange}
                  >
                    <option value={90}>90% — reduce if I miss by more than 10%</option>
                    <option value={75}>75% — reduce if I miss by more than 25% (recommended)</option>
                    <option value={60}>60% — only reduce if I'm well below target</option>
                    <option value={50}>50% — only reduce if I'm running half my target</option>
                  </select>
                  <span className="field-hint">
                    If your recent runs consistently fall below this % of your stated weekly mileage,
                    the plan adapts down to match your actual fitness.
                  </span>
                </div>
                <div className="form-group">
                  <label>Max Heart Rate</label>
                  <input
                    type="number"
                    min="100"
                    max="220"
                    name="max_heart_rate"
                    value={formData.max_heart_rate || ''}
                    onChange={handleChange}
                    placeholder="e.g., 185"
                    className={fieldErrors.max_heart_rate ? 'error-input' : ''}
                  />
                  {fieldErrors.max_heart_rate && (
                    <span className="field-error-msg">{fieldErrors.max_heart_rate}</span>
                  )}
                  <span className="field-hint">Optional — helps set training zones (100–220 bpm)</span>
                </div>
                <div className="form-group">
                  {/* Empty space for grid alignment */}
                </div>
              </div>
            </section>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="rg-btn-cancel"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rg-btn-save"
                disabled={saving}
              >
                {saving ? 'Saving & generating plan...' : 'Save & Update Plan'}
              </button>
            </div>
          </form>
          )}
        </div>
      </div>
    </>
  );
};

export default RaceGoalSetup;
